import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { GridApi } from 'ag-grid-community';
import { NgxSpinnerService } from 'ngx-spinner';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { PayrollPeriodService } from 'src/app/services/payroll-period.service';
import { UtilsService } from 'src/app/services/utils.service';
import { PermissionService } from 'src/app/services/permission.service';

const PERIODICITY_OPTIONS = ['Semanal', 'Catorcenal', 'Quincenal', 'Mensual'];
const CALCULATION_TYPE_OPTIONS = ['Diario', 'Semanal', 'Quincenal', 'Mensual'];
const PAYROLL_TYPE_OPTIONS = ['Normal'];
const STATUS_OPTIONS = [
    { value: '', label: 'Todos' },
    { value: 'open', label: 'Abierto' },
    { value: 'closed', label: 'Cerrado' },
];

@Component({
    selector: 'app-payroll-periods-list',
    templateUrl: './payroll-periods-list.component.html',
    styleUrls: ['./payroll-periods-list.component.css']
})
export class PayrollPeriodsListComponent implements OnInit {

    readonly periodicityOptions = PERIODICITY_OPTIONS;
    readonly calculationTypeOptions = CALCULATION_TYPE_OPTIONS;
    readonly payrollTypeOptions = PAYROLL_TYPE_OPTIONS;
    readonly statusOptions = STATUS_OPTIONS;
    readonly yearOptions: number[] = (() => {
        const current = new Date().getFullYear();
        const years: number[] = [];
        for (let y = current + 1; y >= current - 4; y--) years.push(y);
        return years;
    })();

    filterYear: number = new Date().getFullYear();
    filterStatus = '';
    filterPeriodicity = '';
    filterPayrollType = '';
    filterCalculationType = '';
    private filtersChanged$ = new Subject<void>();

    rowData: any[] = [];
    stats = { total_periods: 0, open: 0, closed: 0, cfdi_not_generated: 0, cfdi_in_stamping: 0 };

    private gridApi!: GridApi;

    columnDefs = [
        { headerName: '', width: 50, checkboxSelection: true, sortable: false, filter: false, resizable: false },
        {
            field: 'period_date', headerName: 'Periodo', flex: 1, sortable: false, filter: false,
            cellRenderer: (params: any) => `<a href="javascript:void(0)" class="period-date-link">${this.formatDate(params.value)}</a>`
        },
        {
            field: 'status', headerName: 'Estado', flex: 1, sortable: false, filter: false,
            cellRenderer: (params: any) => this.renderStatusBadge(params.value)
        },
        { field: 'payroll_number', headerName: 'N° de nómina', flex: 1, sortable: false, filter: false },
        { field: 'periodicity', headerName: 'Periodicidad', flex: 1, sortable: false, filter: false },
        { field: 'payroll_type', headerName: 'Tipo de nómina', flex: 1, sortable: false, filter: false },
        { field: 'calculation_type', headerName: 'Tipo de cálculo', flex: 1, sortable: false, filter: false },
        {
            field: 'mid_period_increase', headerName: 'Incremento mitad nómina', flex: 1, sortable: false, filter: false,
            cellRenderer: (params: any) => this.renderBoolBadge(params.value)
        },
        {
            field: 'cfdi_not_generated_count', headerName: "CFDI's no generados", flex: 1, sortable: false, filter: false,
            cellRenderer: (params: any) => this.renderCountBadge(params.value)
        },
        { field: 'cfdi_stamped_count', headerName: "CFDI's en timbrado", flex: 1, sortable: false, filter: false },
    ];

    public defaultColDef = {
        sortable: false,
        filter: false,
        resizable: true,
    };

    public noRowsTemplate = '<span style="padding: 24px; color: #6b7280;">No se encontraron periodos de nómina</span>';

    public pageSize = 15;
    public pageSizeOptions: number[] = [10, 15, 25, 50];
    public currentPage = 1;
    public lastPage = 1;
    public totalRows = 0;

    constructor(
        private router: Router,
        private payrollPeriodService: PayrollPeriodService,
        private utils: UtilsService,
        private spinner: NgxSpinnerService,
        private permissionService: PermissionService
    ) { }

    canDo(action: string): boolean {
        return this.permissionService.canDo('Administrador de periodos', action);
    }

    ngOnInit(): void {
        this.filtersChanged$.pipe(debounceTime(350)).subscribe(() => {
            this.currentPage = 1;
            this.fetchData();
        });
        this.fetchData();
    }

    onGridReady(params: any) {
        this.gridApi = params.api;
        // ag-grid no ejecuta correctamente los cellRenderer de función en el
        // primer render cuando columnDefs llega vía el binding [columnDefs] de
        // Angular; el setTimeout empuja la reaplicación fuera del ciclo de
        // detección de cambios en curso (mismo bug ya documentado en
        // payroll-receipts-list/employee-list/payroll-list).
        setTimeout(() => params.api.setColumnDefs(this.columnDefs));
    }

    onFiltersChanged() {
        this.filtersChanged$.next();
    }

    onCellClicked(event: any) {
        if (event?.colDef?.field === 'period_date' && event?.data?.period_date) {
            const isoDate = String(event.data.period_date).split('T')[0].split(' ')[0];
            this.router.navigate(['/payroll-receipts'], { queryParams: { period: isoDate } });
        }
    }

    fetchData(): void {
        this.spinner.show();
        const params: any = {
            page: this.currentPage,
            per_page: this.pageSize,
        };
        if (this.filterYear) params.year = this.filterYear;
        if (this.filterStatus) params.status = this.filterStatus;
        if (this.filterPeriodicity) params.periodicity = this.filterPeriodicity;
        if (this.filterPayrollType) params.payroll_type = this.filterPayrollType;
        if (this.filterCalculationType) params.calculation_type = this.filterCalculationType;

        this.payrollPeriodService.list(params).subscribe({
            next: (resp: any) => {
                const paginator = resp?.data || {};
                this.rowData = Array.isArray(paginator.data) ? paginator.data : [];
                this.currentPage = paginator.current_page || this.currentPage;
                this.lastPage = paginator.last_page || 1;
                this.totalRows = paginator.total ?? this.rowData.length;
                this.stats = resp?.stats || this.stats;
                this.spinner.hide();
                setTimeout(() => this.gridApi?.setColumnDefs(this.columnDefs));
            },
            error: () => {
                this.rowData = [];
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Error', text: 'No se pudo obtener el listado de periodos de nómina', icon: 'error' });
            }
        });
    }

    refreshGrid() {
        this.fetchData();
    }

    onPageSizeChanged(newSize: number | string) {
        const size = typeof newSize === 'string' ? parseInt(newSize, 10) : newSize;
        if (!isNaN(size)) {
            this.pageSize = size;
            this.currentPage = 1;
            this.fetchData();
        }
    }

    nextPage() {
        if (this.currentPage < this.lastPage) {
            this.currentPage += 1;
            this.fetchData();
        }
    }

    prevPage() {
        if (this.currentPage > 1) {
            this.currentPage -= 1;
            this.fetchData();
        }
    }

    nuevoPeriodo(): void {
        this.spinner.show();
        this.payrollPeriodService.create({}).subscribe({
            next: () => {
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Operación exitosa', text: 'Se creó el nuevo periodo de nómina', icon: 'success' });
                this.fetchData();
            },
            error: (err: any) => {
                this.spinner.hide();
                // ApiService.handleError() ya reduce el error HTTP a un string
                // plano (ver src/app/shared/api.service.ts) — err NO es un
                // HttpErrorResponse aquí, así que no hay err.error.message.
                const text = (typeof err === 'string' && err) || 'No se pudo crear el periodo de nómina';
                this.utils.alertSwal({ title: 'Error', text, icon: 'error' });
            }
        });
    }

    exportar(): void {
        if (!this.rowData.length) {
            this.utils.alertSwal({ title: 'Sin datos', text: 'No hay periodos para exportar', icon: 'info' });
            return;
        }

        const headers = ['Periodo', 'Estado', 'N° de nómina', 'Periodicidad', 'Tipo de nómina', 'Tipo de cálculo', 'Incremento mitad nómina', 'CFDI no generados', 'CFDI en timbrado'];
        const rows = this.rowData.map(r => [
            this.formatDate(r.period_date),
            r.status === 'open' ? 'Abierto' : 'Cerrado',
            r.payroll_number,
            r.periodicity,
            r.payroll_type,
            r.calculation_type,
            r.mid_period_increase ? 'Sí' : 'No',
            r.cfdi_not_generated_count,
            r.cfdi_stamped_count,
        ]);
        const csv = [headers, ...rows]
            .map(row => row.map((v: any) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))
            .join('\n');

        const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `periodos-nomina-${this.filterYear}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    }

    // ---- Helpers ----
    formatDate(value: any): string {
        if (!value) return '--/--/----';
        const datePart = String(value).split('T')[0].split(' ')[0];
        const [yyyy, mm, dd] = datePart.split('-');
        if (!yyyy || !mm || !dd) return '--/--/----';
        return `${dd}/${mm}/${yyyy}`;
    }

    renderStatusBadge(value: string): string {
        if (value === 'open') {
            return `<span class="period-status-badge period-status-open"><span class="period-status-dot"></span>Abierto</span>`;
        }
        return `<span class="period-status-badge period-status-closed"><span class="period-status-dot"></span>Cerrado</span>`;
    }

    renderBoolBadge(value: boolean): string {
        return value
            ? `<span class="period-bool-yes">Sí</span>`
            : `<span class="period-bool-no">No</span>`;
    }

    renderCountBadge(value: number): string {
        if (!value) return `<span>0</span>`;
        return `<span class="period-count-badge">${value}</span>`;
    }
}
